import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { RecordMode, TimeProjectRef, TimeRow, TimeTagRef, TimeTaskRef } from "@/lib/timer";

type RunningFieldPatch = {
	readonly billable?: boolean;
	readonly name?: string | null;
	readonly project?: TimeProjectRef | null;
	readonly tags?: readonly TimeTagRef[];
	readonly task?: TimeTaskRef | null;
};

type DraftFields = {
	readonly billable: boolean;
	readonly name: string;
	readonly project: TimeProjectRef | null;
	readonly tags: readonly TimeTagRef[];
	readonly task: TimeTaskRef | null;
};

const sameTags = (a: readonly TimeTagRef[], b: readonly TimeTagRef[]): boolean =>
	a.length === b.length && a.every((tag, index) => tag.id === b[index]?.id);

type StoredRunning = Omit<TimeRow, "tags"> & { tags: TimeTagRef[] };

const copyRunning = (running: TimeRow): StoredRunning => ({
	id: running.id,
	name: running.name,
	start: running.start,
	end: running.end,
	timeDiff: running.timeDiff,
	billable: running.billable,
	inputMode: running.inputMode,
	project: running.project,
	task: running.task,
	client: running.client,
	tags: [...running.tags],
	hasTimeOverlap: running.hasTimeOverlap,
	currentUserCanEdit: running.currentUserCanEdit,
});

const clearDraftFields = (state: {
	billable: boolean;
	draftEpoch: number;
	name: string;
	project: TimeProjectRef | null;
	tags: TimeTagRef[];
	task: TimeTaskRef | null;
}): void => {
	state.name = "";
	state.project = null;
	state.task = null;
	state.tags = [];
	state.billable = true;
	state.draftEpoch += 1;
};

interface RecordToolbarState {
	/** Mirror an open recording's row edits into the toolbar draft before (or without) a save. */
	adoptRunningDraft: (id: string, fields: DraftFields) => void;
	billable: boolean;
	clear: () => void;
	/** Bumped on hydrate / clear / setRunning so RHF can reset from store. */
	draftEpoch: number;
	hydrateFromEntry: (entry: TimeRow) => void;
	isPlaying: boolean;
	mode: RecordMode;
	name: string;
	/** Update open recording fields without resetting the draft / draftEpoch. */
	patchRunning: (patch: RunningFieldPatch) => StoredRunning | null;
	project: TimeProjectRef | null;
	running: StoredRunning | null;
	setBillable: (billable: boolean) => void;
	setMode: (mode: RecordMode) => void;
	setName: (name: string) => void;
	setProject: (project: TimeProjectRef | null) => void;
	setRunning: (running: TimeRow | null) => void;
	setTags: (tags: readonly TimeTagRef[]) => void;
	setTask: (task: TimeTaskRef | null) => void;
	/**
	 * Align running + draft with a saved open row. Only bumps draftEpoch for fields
	 * that actually changed on the row (so local project picks don't wipe a typed description).
	 */
	syncRunningFromRow: (row: TimeRow) => void;
	tags: TimeTagRef[];
	task: TimeTaskRef | null;
	toggleBillable: () => void;
}

export const useRecordToolbarStore = create<RecordToolbarState>()(
	immer((set, get) => ({
		name: "",
		project: null,
		task: null,
		billable: true,
		tags: [] as TimeTagRef[],
		mode: "timer" as const,
		isPlaying: false,
		running: null,
		draftEpoch: 0,
		setName: (name) => {
			set((state) => {
				state.name = name;
			});
		},
		setProject: (project) => {
			set((state) => {
				state.project = project;
				state.task = null;
				if (project && typeof project.billable === "boolean") {
					state.billable = project.billable;
				}
			});
		},
		setTask: (task) => {
			set((state) => {
				state.task = task;
			});
		},
		setTags: (tags) => {
			set((state) => {
				state.tags = [...tags];
			});
		},
		setBillable: (billable) => {
			set((state) => {
				state.billable = billable;
			});
		},
		toggleBillable: () => {
			set((state) => {
				state.billable = !state.billable;
			});
		},
		setMode: (mode) => {
			set((state) => {
				state.mode = mode;
			});
		},
		setRunning: (running) => {
			set((state) => {
				const hadOpen = !!state.running && !state.running.end;
				state.running = running ? copyRunning(running) : null;
				state.isPlaying = !!running && !running.end;
				if (running && !running.end) {
					state.name = running.name ?? "";
					state.project = running.project;
					state.task = running.task;
					state.billable = running.billable;
					state.tags = [...running.tags];
					state.draftEpoch += 1;
					return;
				}
				if (hadOpen) {
					clearDraftFields(state);
				}
			});
		},
		patchRunning: (patch) => {
			set((state) => {
				if (!state.running || state.running.end) {
					return;
				}
				if (patch.project !== undefined) {
					state.running.project = patch.project;
				}
				if (patch.task !== undefined) {
					state.running.task = patch.task;
				}
				if (patch.billable !== undefined) {
					state.running.billable = patch.billable;
				}
				if (patch.name !== undefined) {
					state.running.name = patch.name;
				}
				if (patch.tags !== undefined) {
					state.running.tags = [...patch.tags];
				}
			});
			const running = get().running;
			return running && !running.end ? copyRunning(running) : null;
		},
		syncRunningFromRow: (row) => {
			set((state) => {
				if (state.running?.id !== row.id) {
					return;
				}
				if (row.end) {
					state.running = null;
					state.isPlaying = false;
					clearDraftFields(state);
					return;
				}
				const prev = state.running;
				if (!prev) {
					return;
				}
				const nameChanged = (prev.name ?? "") !== (row.name ?? "");
				const projectChanged = prev.project?.id !== row.project?.id;
				const taskChanged = prev.task?.id !== row.task?.id;
				const billableChanged = prev.billable !== row.billable;
				const tagsChanged = !sameTags(prev.tags, row.tags);
				const draftMatches =
					(state.name ?? "") === (row.name ?? "") &&
					state.project?.id === row.project?.id &&
					state.task?.id === row.task?.id &&
					state.billable === row.billable &&
					sameTags(state.tags, row.tags);
				state.running = copyRunning(row);
				state.isPlaying = true;
				if (draftMatches || !(nameChanged || projectChanged || taskChanged || billableChanged || tagsChanged)) {
					return;
				}
				if (nameChanged) {
					state.name = row.name ?? "";
				}
				if (projectChanged) {
					state.project = row.project;
					state.task = row.task;
				} else if (taskChanged) {
					state.task = row.task;
				}
				if (billableChanged) {
					state.billable = row.billable;
				}
				if (tagsChanged) {
					state.tags = [...row.tags];
				}
				state.draftEpoch += 1;
			});
		},
		adoptRunningDraft: (id, fields) => {
			set((state) => {
				if (state.running?.id !== id || state.running.end) {
					return;
				}
				if (
					state.name === fields.name &&
					state.project?.id === fields.project?.id &&
					state.task?.id === fields.task?.id &&
					state.billable === fields.billable &&
					sameTags(state.tags, fields.tags)
				) {
					return;
				}
				state.name = fields.name;
				state.project = fields.project;
				state.task = fields.task;
				state.billable = fields.billable;
				state.tags = [...fields.tags];
				state.draftEpoch += 1;
			});
		},
		hydrateFromEntry: (entry) => {
			set((state) => {
				state.name = entry.name ?? "";
				state.project = entry.project;
				state.task = entry.task;
				state.billable = entry.billable;
				state.tags = [...entry.tags];
				state.mode = "timer" as const;
				state.draftEpoch += 1;
			});
		},
		clear: () => {
			set((state) => {
				clearDraftFields(state);
				state.isPlaying = false;
				state.running = null;
			});
		},
	}))
);
